import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { BrowserQRCodeReader } from '@zxing/browser';
import { auth, functions } from '../../firebase-client';
import { useOnlineStatus } from '../../utils/onlineStatus';
import {
  cacheRideDeclarationCheckerPermit,
  listRideDeclarationOfflineChecks,
  queueRideDeclarationOfflineCheck,
  readRideDeclarationCheckerPermit,
  removeRideDeclarationOfflineCheck,
  verifyRideDeclarationOfflineCredential,
} from '../../utils/rideDeclarationOffline';
import './rideDeclarations.css';

const previewProof = httpsCallable(functions, 'previewRideDeclarationProof');
const recordCheck = httpsCallable(functions, 'recordRideDeclarationCheck');
const prepareOfflineChecker = httpsCallable(functions, 'prepareRideDeclarationOfflineChecker');
const syncOfflineCheck = httpsCallable(functions, 'syncOfflineRideDeclarationCheck');

function extractCode(value) {
  const text = String(value || '').trim();
  if (text.startsWith('MNRD1.')) return text;
  try { return new URL(text).searchParams.get('code') || ''; } catch { return text.toUpperCase().replace(/[^A-Z2-9]/g, ''); }
}

function millis(value) {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const seconds = Number(value?.seconds ?? value?._seconds);
  const nanoseconds = Number(value?.nanoseconds ?? value?._nanoseconds ?? 0);
  if (Number.isFinite(seconds) && Number.isFinite(nanoseconds)) return (seconds * 1000) + Math.floor(nanoseconds / 1000000);
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function dateTime(value) {
  const valueMillis = millis(value); return valueMillis ? new Date(valueMillis).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

export default function RideDeclarationVerify() {
  const { teamId } = useParams(); const [params, setParams] = useSearchParams(); const online = useOnlineStatus(); const videoRef = useRef(null); const controlsRef = useRef(null); const scanLock = useRef(false);
  const initialPreviewAttemptedRef = useRef(false); const userId = auth.currentUser?.uid || '';
  const [code, setCode] = useState(() => extractCode(params.get('code'))); const [preview, setPreview] = useState(null); const [checkerBundle, setCheckerBundle] = useState(null);
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [success, setSuccess] = useState(null); const [scanning, setScanning] = useState(false); const [pendingCount, setPendingCount] = useState(0);

  const stopScanner = useCallback(() => { controlsRef.current?.stop(); controlsRef.current = null; setScanning(false); scanLock.current = false; }, []);
  useEffect(() => () => controlsRef.current?.stop(), []);

  const loadCheckerBundle = useCallback(async () => {
    if (!userId) return null;
    if (online) {
      try {
        const result = await prepareOfflineChecker({ teamId });
        await cacheRideDeclarationCheckerPermit({ teamId, userId, permit: result.data }); setCheckerBundle(result.data); return result.data;
      } catch (err) { setError(err?.message || 'Could not prepare offline checking.'); return null; }
    }
    const cached = await readRideDeclarationCheckerPermit({ teamId, userId }).catch(() => null); setCheckerBundle(cached); return cached;
  }, [online, teamId, userId]);

  const syncQueue = useCallback(async ({ quiet = false } = {}) => {
    if (!online || !userId) return;
    const queued = await listRideDeclarationOfflineChecks({ teamId, userId }).catch(() => []); setPendingCount(queued.length);
    for (const item of queued) {
      try {
        await syncOfflineCheck({ teamId, offlineCheckId: item.id, credential: item.credential, checkerPermit: item.checkerPermit, checkedAt: item.checkedAt });
        await removeRideDeclarationOfflineCheck(item.id);
      } catch (err) {
        if (!quiet) setError(`An offline check needs attention: ${err?.message || 'sync failed'}`);
        break;
      }
    }
    const remaining = await listRideDeclarationOfflineChecks({ teamId, userId }).catch(() => []); setPendingCount(remaining.length);
  }, [online, teamId, userId]);

  useEffect(() => { loadCheckerBundle(); }, [loadCheckerBundle]);
  useEffect(() => { if (online) syncQueue({ quiet: true }); else if (userId) listRideDeclarationOfflineChecks({ teamId, userId }).then(rows => setPendingCount(rows.length)).catch(() => {}); }, [online, syncQueue, teamId, userId]);

  const previewCode = useCallback(async (input) => {
    const nextCode = extractCode(input); if (!nextCode || scanLock.current) return;
    scanLock.current = true; setBusy(true); setError(''); setSuccess(null);
    try {
      if (nextCode.startsWith('MNRD1.')) {
        const bundle = checkerBundle || await loadCheckerBundle();
        if (!bundle) throw new Error('Reconnect once to authorise this device for offline checking.');
        const payload = await verifyRideDeclarationOfflineCredential(nextCode, bundle.verificationKey, { expectedType: 'member', teamId });
        setCode(''); setPreview({ mode: 'offline', credential: nextCode, memberName: payload.nm || 'Member', membershipNumber: payload.mn || '', versionNumber: Number(payload.n || 0), acceptedAt: Number(payload.a || 0), expiresAt: Number(payload.exp || 0), teamName: bundle.teamName }); stopScanner();
      } else {
        if (!online) throw new Error('Manual proof codes require signal. Scan the member’s saved offline QR instead.');
        const result = await previewProof({ code: nextCode }); setCode(nextCode); setPreview({ ...result.data, mode: 'live' }); setParams({ code: nextCode }, { replace: true }); stopScanner();
      }
    } catch (err) { setPreview(null); setError(err?.message || 'This proof could not be verified.'); }
    finally { setBusy(false); scanLock.current = false; }
  }, [checkerBundle, loadCheckerBundle, online, setParams, stopScanner, teamId]);

  useEffect(() => {
    if (initialPreviewAttemptedRef.current || !code || code.startsWith('MNRD1.')) return;
    initialPreviewAttemptedRef.current = true; previewCode(code);
  }, [code, previewCode]);

  const startScanner = async () => {
    setError(''); setPreview(null); setScanning(true);
    try {
      const reader = new BrowserQRCodeReader(); controlsRef.current = await reader.decodeFromConstraints({ video: { facingMode: { ideal: 'environment' } }, audio: false }, videoRef.current, (result) => { if (result?.getText()) previewCode(result.getText()); });
    } catch { setScanning(false); setError(online ? 'Camera access is unavailable. Enter the manual code instead.' : 'Camera access is unavailable. Offline checks require the member’s QR code.'); }
  };

  const record = async () => {
    setBusy(true); setError('');
    try {
      if (preview.mode === 'offline') {
        const bundle = checkerBundle || await loadCheckerBundle();
        if (!bundle) throw new Error('This device is not prepared for offline checking.');
        const checkedAt = Date.now();
        const queued = await queueRideDeclarationOfflineCheck({ teamId, userId, credential: preview.credential, checkerPermit: bundle.permit, checkedAt, memberName: preview.memberName, versionNumber: preview.versionNumber });
        let pendingSync = true;
        if (online) {
          try {
            const result = await syncOfflineCheck({ teamId, offlineCheckId: queued.id, credential: queued.credential, checkerPermit: queued.checkerPermit, checkedAt: queued.checkedAt });
            await removeRideDeclarationOfflineCheck(queued.id); pendingSync = false; setSuccess({ ...result.data, pendingSync: false });
          } catch { setSuccess({ memberName: preview.memberName, versionNumber: preview.versionNumber, checkedAt, pendingSync: true }); }
        } else setSuccess({ memberName: preview.memberName, versionNumber: preview.versionNumber, checkedAt, pendingSync: true });
        const remaining = await listRideDeclarationOfflineChecks({ teamId, userId }).catch(() => []); setPendingCount(remaining.length);
        if (pendingSync && online) syncQueue({ quiet: true });
      } else {
        const result = await recordCheck({ code }); setSuccess({ ...result.data, pendingSync: false });
      }
      setPreview(null); setCode(''); setParams({}, { replace: true });
    } catch (err) { setError(err?.message || 'Could not record this check.'); }
    finally { setBusy(false); }
  };

  return <main className="ride-verify-page"><header><Link className="btn btn--secondary" to={`/teams/${teamId}`}>Back to club</Link><span>IAM RoadSmart</span><h1>Check ride declaration</h1><p>Scan the member’s proof, verify their details, then record the pre-ride check.</p></header>
    {!online && <div className="ride-proof-alert">Offline checking is available with a saved checker authorisation. Scan the member’s offline QR; the record will sync automatically.</div>}
    {pendingCount > 0 && <div className="ride-proof-alert" role="status">{pendingCount} offline {pendingCount === 1 ? 'check is' : 'checks are'} waiting to sync.</div>}
    {success && <section className="ride-verify-success" role="status"><strong>✓ Pre-ride check recorded</strong><span>{success.memberName} · declaration version {success.versionNumber}</span><small>{success.pendingSync ? `Saved securely on this device at ${dateTime(success.checkedAt)}. It will sync when signal returns.` : `Saved to the member’s declaration history at ${dateTime(success.checkedAt)}.`}</small></section>}{error && <p className="error-message" role="alert">{error}</p>}
    <section className="ride-scanner"><div className={`ride-scanner__video ${scanning ? 'is-active' : ''}`}><video ref={videoRef} muted playsInline /><span>{scanning ? 'Point the camera at the declaration QR' : 'Camera scanner is off'}</span></div><button className="button button-secondary" type="button" onClick={scanning ? stopScanner : startScanner}>{scanning ? 'Stop scanner' : 'Start QR scanner'}</button></section>
    <section className="ride-manual-code"><label>Manual live proof code<input className="form-input" value={code.startsWith('MNRD1.') ? '' : code} onChange={(event) => setCode(extractCode(event.target.value))} maxLength="12" autoCapitalize="characters" placeholder="12-character code" disabled={!online} /></label><button className="button button-secondary" type="button" onClick={() => previewCode(code)} disabled={busy || !online || code.length !== 12}>{busy ? 'Checking…' : 'Check code'}</button></section>
    {preview && <section className="ride-verify-preview"><span className="ride-status ride-status--current">{preview.mode === 'offline' ? 'Verified offline' : 'Live and current'}</span><h2>{preview.memberName}</h2><p>IAM membership {preview.membershipNumber}</p><dl><div><dt>Group</dt><dd>{preview.teamName}</dd></div><div><dt>Declaration</dt><dd>Version {preview.versionNumber}</dd></div><div><dt>Signed</dt><dd>{dateTime(preview.acceptedAt)}</dd></div>{preview.mode === 'offline' && <div><dt>Offline proof expires</dt><dd>{dateTime(preview.expiresAt)}</dd></div>}</dl><button className="button button-primary" type="button" onClick={record} disabled={busy}>{busy ? 'Recording…' : 'Record pre-ride check'}</button><small>Confirm the person presenting this proof matches these details.</small></section>}
  </main>;
}
